/**
 * How the mail list asks for more (Akhil, 2026-09-25).
 *
 * Measured on rfqdev: `mail.get_emails` honours `offset`, caps a page at 100 rows however much
 * is asked for, and its `total` merely echoes the rows returned — it is not a grand total. So
 * there is no page count to display and no way to know how much mail exists; the only signal
 * that more remains is a page coming back FULL.
 *
 * Kept as a pure rule rather than inline in the hook so both the "is there more" question and
 * the next request's offset come from one place.
 */
export const MAIL_PAGE_SIZE = 100

/** The offset for the next request, or `null` when the last page proved there is no more. */
export function nextMailOffset(pages: readonly { rows: number }[]): number | null {
  if (pages.length === 0) return 0
  const last = pages[pages.length - 1]
  if (last.rows < MAIL_PAGE_SIZE) return null
  return pages.reduce((sum, page) => sum + page.rows, 0)
}

/**
 * One row per mail, first occurrence wins.
 *
 * Offset paging over a live mailbox can hand back a row twice: mail that arrives between two
 * page fetches pushes everything down, so the next offset starts one row earlier than it would
 * have. The id is the identity — keeping the first copy holds the reading order steady.
 */
export function dedupeById<T extends { id: string }>(rows: readonly T[]): T[] {
  const seen = new Set<string>()
  const out: T[] = []
  for (const row of rows) {
    if (seen.has(row.id)) continue
    seen.add(row.id)
    out.push(row)
  }
  return out
}
